import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Sorting.module.css'; 
import { generateUnsortedArray } from '../../lib/arrayGenerator';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const BUBBLE_SORT_CODE = `function bubbleSort(arr) {
  let n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j] > arr[j + 1]) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
      }
    }
  }
}`;
const QUICK_SORT_CODE = `function quickSort(arr, low, high) {
  if (low < high) {
    let pi = partition(arr, low, high);
    quickSort(arr, low, pi - 1);
    quickSort(arr, pi + 1, high);
  }
}
function partition(arr, low, high) {
  let pivot = arr[high];
  let i = (low - 1);

  for (let j = low; j < high; j++) {
    if (arr[j] < pivot) {
      i++;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
  [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
  return (i + 1);
}`;
const MERGE_SORT_CODE = `function mergeSort(arr, l, r) {
  if (l >= r) {
    return;
  }
  const m = Math.floor((l + r) / 2);
  mergeSort(arr, l, m);
  mergeSort(arr, m + 1, r);
  merge(arr, l, m, r);
}

function merge(arr, left, mid, right) {
  const leftArr = arr.slice(left, mid + 1);
  const rightArr = arr.slice(mid + 1, right + 1);
  let i = 0, j = 0, k = left;

  while (i < leftArr.length && j < rightArr.length) {
    if (leftArr[i] <= rightArr[j]) {
      arr[k++] = leftArr[i++];
    } else {
      arr[k++] = rightArr[j++];
    }
  }

  while (i < leftArr.length) arr[k++] = leftArr[i++];
  while (j < rightArr.length) arr[k++] = rightArr[j++];
}`;;

type SortState = {
  array: number[];
  highlights: { [key: string]: any };
  sorted: number[];
  time: number | null;
  line: number | null;
};

const CompareSorts: React.FC = () => {
  const navigate = useNavigate();
  const [isSorting, setIsSorting] = useState(false);
  const stopSort = useRef(false);
  const [bubbleSortState, setBubbleSortState] = useState<SortState>({ array: [], highlights: {}, sorted: [], time: null, line: null });
  const [quickSortState, setQuickSortState] = useState<SortState>({ array: [], highlights: {}, sorted: [], time: null, line: null });
  const [mergeSortState, setMergeSortState] = useState<SortState>({ array: [], highlights: {}, sorted: [], time: null, line: null });
  const bubbleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const quickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mergeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const handleGoBack = () => navigate(-1);

  const clearAllTimers = () => {
    if (bubbleTimerRef.current) clearInterval(bubbleTimerRef.current);
    if (quickTimerRef.current) clearInterval(quickTimerRef.current);
    if (mergeTimerRef.current) clearInterval(mergeTimerRef.current);
  };

  const handleRandomize = useCallback(() => {
    stopSort.current = true;
    clearAllTimers();
    setIsSorting(false);
    const newArray = generateUnsortedArray(20, 100);
    const initialState = { array: newArray, highlights: {}, sorted: [], time: null, line: null };
    setBubbleSortState(initialState);
    setQuickSortState(initialState);
    setMergeSortState(initialState);
  }, []);

  useEffect(() => {
    handleRandomize();
    return () => { stopSort.current = true; clearAllTimers(); };
  }, [handleRandomize]);
  
  const runComparison = async () => {
    stopSort.current = false;
    const newArray = generateUnsortedArray(20, 100);
    const cleanState = { array: newArray, highlights: {}, sorted: [], time: 0, line: null };
    setBubbleSortState(cleanState);
    setQuickSortState(cleanState);
    setMergeSortState(cleanState);
    setIsSorting(true);
    const startTime = Date.now();
    bubbleTimerRef.current = setInterval(() => setBubbleSortState(prev => ({ ...prev, time: Date.now() - startTime })), 50);
    quickTimerRef.current = setInterval(() => setQuickSortState(prev => ({ ...prev, time: Date.now() - startTime })), 50);
    mergeTimerRef.current = setInterval(() => setMergeSortState(prev => ({ ...prev, time: Date.now() - startTime })), 50);

    await Promise.all([
      runBubbleSortAnimation(),
      runQuickSortAnimation(),
      runMergeSortAnimation()
    ]);

    setIsSorting(false);
  };

  const runBubbleSortAnimation = async () => {
    let arr = [...bubbleSortState.array];
    const n = arr.length;
    for (let i = 0; i < n - 1; i++) {
      for (let j = 0; j < n - i - 1; j++) {
        if (stopSort.current) break;
        setBubbleSortState(prev => ({ ...prev, highlights: { comparing: [j, j + 1] }, line: 4 }));
        await delay(300);
        if (arr[j] > arr[j + 1]) {
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
          setBubbleSortState(prev => ({ ...prev, array: [...arr], line: 6 }));
          await delay(300);
        }
      }
      if (stopSort.current) break;
      setBubbleSortState(prev => ({ ...prev, sorted: [...prev.sorted, n - 1 - i] }));
    }
    if (!stopSort.current) setBubbleSortState(prev => ({ ...prev, sorted: arr.map((_, i) => i) }));
    if (bubbleTimerRef.current) clearInterval(bubbleTimerRef.current); 
  };

const runQuickSortAnimation = async () => {
    let arr = [...quickSortState.array];
    await quickSortHelper(arr, 0, arr.length - 1);
    if (!stopSort.current) {
      setQuickSortState(prev => ({ ...prev, sorted: arr.map((_, i) => i) }));
    }
    if (quickTimerRef.current) clearInterval(quickTimerRef.current);
  };

  const quickSortHelper = async (arr: number[], low: number, high: number) => {
    if (stopSort.current) return;
    if (low < high) {
      setQuickSortState(prev => ({ ...prev, line: 4 }));
      const pi = await partition(arr, low, high);
      if (pi === -1) return;

      await quickSortHelper(arr, low, pi - 1);
      if (stopSort.current) return;
      await delay(300);

      await quickSortHelper(arr, pi + 1, high);
      if (stopSort.current) return;
      await delay(300);
    } else {
      if (low >= 0 && low < arr.length) {
         setQuickSortState(prev => ({ ...prev, sorted: [...prev.sorted, low] }));
      }
    }
  };

  const partition = async (arr: number[], low: number, high: number): Promise<number> => {
    if (stopSort.current) return -1;
    const pivot = arr[high];
    let i = low - 1;

    setQuickSortState(prev => ({ ...prev, highlights: { pivot: high }, line: 10 }));
    await delay(300); 

    for (let j = low; j < high; j++) {
      if (stopSort.current) return -1;
      setQuickSortState(prev => ({ ...prev, highlights: { ...prev.highlights, comparing: [j] }, line: 13 }));
      await delay(300); 

      if (arr[j] < pivot) {
        i++;
        [arr[i], arr[j]] = [arr[j], arr[i]];
        setQuickSortState(prev => ({ ...prev, array: [...arr], line: 16 }));
        await delay(300);
      }
    }

    if (stopSort.current) return -1;
    [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
    const partitionIndex = i + 1;
    
    setQuickSortState(prev => ({ 
      ...prev, 
      array: [...arr], 
      sorted: [...prev.sorted, partitionIndex], 
      highlights: {},
      line: 19 
    }));
    await delay(300); 
    
    return partitionIndex;
  };

  const runMergeSortAnimation = async () => {
    let arr = [...mergeSortState.array];
    const auxiliaryArray = arr.slice();
    await mergeSortHelper(arr, 0, arr.length - 1, auxiliaryArray);
    if (!stopSort.current) setMergeSortState(prev => ({ ...prev, sorted: arr.map((_, i) => i) }));
    if (mergeTimerRef.current) clearInterval(mergeTimerRef.current); 
  };

  const mergeSortHelper = async (mainArray: number[], startIdx: number, endIdx: number, auxiliaryArray: number[]) => {
    if (stopSort.current) return;
    setMergeSortState(prev => ({ ...prev, line: 2 }));
    await delay(300);
    if (startIdx === endIdx) return;

    setMergeSortState(prev => ({ ...prev, line: 5 }));
    const middleIdx = Math.floor((startIdx + endIdx) / 2);
    await delay(300);

    setMergeSortState(prev => ({ ...prev, line: 6 }));
    await mergeSortHelper(auxiliaryArray, startIdx, middleIdx, mainArray);
    if (stopSort.current) return;
    await delay(300); 

    setMergeSortState(prev => ({ ...prev, line: 7 }));
    await mergeSortHelper(auxiliaryArray, middleIdx + 1, endIdx, mainArray);
    if (stopSort.current) return;
    await delay(300);

    setMergeSortState(prev => ({ ...prev, line: 8 }));
    await merge(mainArray, startIdx, middleIdx, endIdx, auxiliaryArray);
    if (stopSort.current) return;
    await delay(300); 
  };

  const merge = async (mainArray: number[], startIdx: number, middleIdx: number, endIdx: number, auxiliaryArray: number[]) => {
    if (stopSort.current) return;
    let k = startIdx; let i = startIdx; let j = middleIdx + 1;
    while (i <= middleIdx && j <= endIdx) {
      if (stopSort.current) return;
      setMergeSortState(prev => ({ ...prev, highlights: { comparing: [i, j] }, line: 18 }));
      await delay(300);

      if (auxiliaryArray[i] <= auxiliaryArray[j]) {
        mainArray[k++] = auxiliaryArray[i++];
      } else {
        mainArray[k++] = auxiliaryArray[j++];
      }
      setMergeSortState(prev => ({...prev, array: [...mainArray]}));
      await delay(300);
    }
    while (i <= middleIdx) {
      if (stopSort.current) return;
      mainArray[k++] = auxiliaryArray[i++];
      setMergeSortState(prev => ({...prev, array: [...mainArray]}));
      await delay(300);
    }
    while (j <= endIdx) {
      if (stopSort.current) return;
      mainArray[k++] = auxiliaryArray[j++];
      setMergeSortState(prev => ({...prev, array: [...mainArray]}));
      await delay(300);
    }
  };
return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
      <h1 className={styles.title}>Compare Sorting Algorithms</h1>
        <p className={styles.description}>
        Imagine you're handed a completely shuffled deck of cards to sort. You could use <b>Bubble Sort</b>, which is like repeatedly 
        looking at two adjacent cards and swapping them if they're in the wrong order—simple, but very slow. 
        A faster way is <b>Quick Sort</b>, where you pick a card as a "pivot" and quickly make two piles (cards lower and higher than the pivot), 
        then repeat on each pile. Or, you could use <b>Merge Sort</b>, where you split the deck into individual cards and then methodically merge them back together in perfect order, 
        which is always reliably fast but requires more table space. 
        </p>
      <p className={styles.disclaimer}>
        *Note: The timers are set to execute per second for demonstration purposes only. This visualizer uses a small, controlled dataset to demonstrate how the algorithms work. 
        In this environment, the actual time differences may be small. The true performance of algorithms like Quick Sort and Merge Sort become dramatically clear 
        only when sorting thousands or millions of items, where Bubble Sort would be far too slow to be practical.
      </p>
      <div className={styles.controls}>
        <button onClick={runComparison} disabled={isSorting}>Run Comparison</button>
        <button onClick={handleRandomize} disabled={isSorting}>Randomize</button>
        <button onClick={() => stopSort.current = true} disabled={!isSorting}>Stop</button>
      </div>

      <div className={styles.mainVisualizationArea}>
        <div className={styles.vizContainer}>
          <h3>Bubble Sort</h3>
          <div className={styles.barChart}>
            {bubbleSortState.array.map((value, index) => (
              <div 
                key={index} 
                className={`
                  ${styles.bar} 
                  ${bubbleSortState.highlights.comparing?.includes(index) ? styles.comparing : ''} 
                  ${bubbleSortState.sorted.includes(index) ? styles.sorted : ''}
                `} 
                style={{height: `${value * 2}px`}}
              >
                <span className={styles.barValue}>{value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.vizContainer}>
          <h3>Quick Sort</h3>
          <div className={styles.barChart}>
            {quickSortState.array.map((value, index) => (
              <div 
                key={index} 
                className={`
                  ${styles.bar} 
                  ${quickSortState.highlights.pivot === index ? styles.pivot : ''} 
                  ${quickSortState.highlights.comparing?.includes(index) ? styles.comparing : ''}
                  ${quickSortState.sorted.includes(index) ? styles.sorted : ''}
                `} 
                style={{height: `${value * 2}px`}}
              >
                <span className={styles.barValue}>{value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.vizContainer}>
          <h3>Merge Sort</h3>
          <div className={styles.barChart}>
            {mergeSortState.array.map((value, index) => (
              <div 
                key={index} 
                className={`
                  ${styles.bar} 
                  ${mergeSortState.highlights.comparing?.includes(index) ? styles.comparing : ''} 
                  ${mergeSortState.sorted.includes(index) ? styles.sorted : ''}
                `} 
                style={{height: `${value * 2}px`}}
              >
                <span className={styles.barValue}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.statsContainer}>
        <div className={styles.algorithmCard}>
          <h2>Bubble Sort</h2>
          <p>Time taken: <b>{bubbleSortState.time !== null ? `${(bubbleSortState.time / 1000).toFixed(2)}s` : '...'}</b></p>
          <div className={styles.codeDisplay}>
            {BUBBLE_SORT_CODE.split('\n').map((line, index) => (
              <div key={index} className={`${styles.codeLine} ${bubbleSortState.line === index + 1 ? styles.highlightedLine : ''}`}>{line}</div>
            ))}
          </div>
        </div>
        <div className={styles.algorithmCard}>
          <h2>Quick Sort</h2>
          <p>Time taken: <b>{quickSortState.time !== null ? `${(quickSortState.time / 1000).toFixed(2)}s` : '...'}</b></p>
          <div className={styles.codeDisplay}>
             {QUICK_SORT_CODE.split('\n').map((line, index) => (
              <div key={index} className={`${styles.codeLine} ${quickSortState.line === index + 1 ? styles.highlightedLine : ''}`}>{line}</div>
            ))}
          </div>
        </div>
        <div className={styles.algorithmCard}>
          <h2>Merge Sort</h2>
          <p>Time taken: <b>{mergeSortState.time !== null ? `${(mergeSortState.time / 1000).toFixed(2)}s` : '...'}</b></p>
          <div className={styles.codeDisplay}>
             {MERGE_SORT_CODE.split('\n').map((line, index) => (
              <div key={index} className={`${styles.codeLine} ${mergeSortState.line === index + 1 ? styles.highlightedLine : ''}`}>{line}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareSorts;

